import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTheme, ACCENT } from '../contexts/ThemeContext';
import CorosLogo from './CorosLogo';

const navItems = [
  { path: '/', label: 'Dashboard' },
  { path: '/csat', label: 'CSAT Review' },
  { path: '/investigate', label: 'Investigate' },
];

function CorosHexLogo({ size = 20 }) {
  return (
    <svg viewBox="0 0 80 90" fill="currentColor" style={{ width: size, height: size * 1.125, color: ACCENT, transition: 'transform 0.3s cubic-bezier(0.34,1.56,0.64,1)' }}
      onMouseEnter={e => e.currentTarget.style.transform = 'rotate(-12deg) scale(1.1)'}
      onMouseLeave={e => e.currentTarget.style.transform = ''}>
      <path d="M8.51 57.53L35.78 73.23L63.41 61.51V67.12L32.34 85L0 66.39V30.62L5.05 27.5L8.5 57.52ZM47.9 11.8L20.67 27.56L17 57.36L12.14 54.55L12.19 18.7L44.48 0L75.44 17.88L75.62 23.82L47.89 11.8ZM67.8 69.11L67.76 37.65L43.79 19.58L48.65 16.77L79.67 34.74L79.72 72.05L48.75 89.93L43.52 87.11L67.8 69.11Z" />
    </svg>
  );
}

/* SCOUT signal mark */
function ScoutMark() {
  return (
    <svg viewBox="0 0 24 24" fill="none" style={{ width: 16, height: 16, color: ACCENT }}>
      <circle cx="12" cy="12" r="3" fill="currentColor" opacity="0.8" />
      <circle cx="12" cy="12" r="7" stroke="currentColor" strokeWidth="1.5" opacity="0.4" />
      <circle cx="12" cy="12" r="11" stroke="currentColor" strokeWidth="1" opacity="0.2" />
    </svg>
  );
}

function LogoutIcon() {
  return (<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></svg>);
}

function SunIcon() {
  return (<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="5"/><line x1="12" y1="1" x2="12" y2="3"/><line x1="12" y1="21" x2="12" y2="23"/><line x1="4.22" y1="4.22" x2="5.64" y2="5.64"/><line x1="18.36" y1="18.36" x2="19.78" y2="19.78"/><line x1="1" y1="12" x2="3" y2="12"/><line x1="21" y1="12" x2="23" y2="12"/><line x1="4.22" y1="19.78" x2="5.64" y2="18.36"/><line x1="18.36" y1="5.64" x2="19.78" y2="4.22"/></svg>);
}

function MoonIcon() {
  return (<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 12.79A9 9 0 1111.21 3 7 7 0 0021 12.79z"/></svg>);
}

export default function Header({ onLogout }) {
  const location = useLocation();
  const { mode, toggle, t } = useTheme();

  return (
    <header style={{
      padding: '0 20px',
      height: '52px',
      borderBottom: `1px solid ${t.border}`,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      position: 'sticky',
      top: 0,
      background: t.bgHeader,
      backdropFilter: 'blur(20px) saturate(180%)',
      WebkitBackdropFilter: 'blur(20px) saturate(180%)',
      zIndex: 100,
    }}>
      {/* Left: Logo + Brand */}
      <Link to="/" style={{ display: 'flex', alignItems: 'center', gap: '10px', textDecoration: 'none' }}>
        <CorosLogo size={24} app="SCOUT" />
      </Link>

      {/* Center: Navigation */}
      <nav style={{ display: 'flex', alignItems: 'center', gap: '0', height: '100%' }}>
        {navItems.map((item) => {
          const isActive = item.path === '/'
            ? location.pathname === '/'
            : location.pathname.startsWith(item.path);
          return (
            <Link
              key={item.path}
              to={item.path}
              style={{
                padding: '0 16px',
                height: '100%',
                display: 'flex',
                alignItems: 'center',
                fontSize: '13px',
                fontWeight: isActive ? 600 : 500,
                letterSpacing: '0.01em',
                textDecoration: 'none',
                color: isActive ? ACCENT : t.textMuted,
                borderBottom: isActive ? `2px solid ${ACCENT}` : '2px solid transparent',
                transition: 'color 0.2s, border-color 0.2s',
              }}
            >
              {item.label}
            </Link>
          );
        })}
      </nav>

      {/* Right: Controls */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        {/* AI Pill */}
        <div style={{
          height: '28px', padding: '0 10px', borderRadius: '8px',
          display: 'flex', alignItems: 'center', gap: '5px',
          fontSize: '11px', fontWeight: 600,
          background: 'rgba(229, 46, 58,0.08)', color: ACCENT,
          border: '1px solid rgba(229, 46, 58,0.1)',
        }}>
          <div style={{
            width: 6, height: 6, borderRadius: '50%',
            background: ACCENT, animation: 'pulse 2s ease-in-out infinite',
          }} />
          <span className="hide-mobile">Intelligence</span>
        </div>

        {/* Theme toggle */}
        <div style={{
          display: 'flex', borderRadius: '8px', overflow: 'hidden',
          background: t.bgInput, border: `1px solid ${t.border}`,
        }}>
          <button
            onClick={() => { if (mode === 'light') toggle(); }}
            style={{
              padding: '6px 12px', border: 'none', fontSize: '11px', fontWeight: 600,
              background: mode === 'dark' ? ACCENT : 'transparent',
              color: mode === 'dark' ? '#fff' : t.textMuted,
              boxShadow: mode === 'dark' ? '0 2px 8px rgba(229, 46, 58,0.3)' : 'none',
              cursor: 'pointer', transition: 'all 0.2s',
            }}
          >Dark</button>
          <button
            onClick={() => { if (mode === 'dark') toggle(); }}
            style={{
              padding: '6px 12px', border: 'none', fontSize: '11px', fontWeight: 600,
              background: mode === 'light' ? ACCENT : 'transparent',
              color: mode === 'light' ? '#fff' : t.textMuted,
              boxShadow: mode === 'light' ? '0 2px 8px rgba(229, 46, 58,0.3)' : 'none',
              cursor: 'pointer', transition: 'all 0.2s',
            }}
          >Light</button>
        </div>

        {onLogout && (
          <button onClick={onLogout} style={{
            background: t.bgInput, border: `1px solid ${t.border}`,
            color: t.textDim, cursor: 'pointer', display: 'flex',
            alignItems: 'center', justifyContent: 'center',
            width: '30px', height: '30px', borderRadius: '8px',
            transition: 'all 0.2s',
          }} title="Sign out">
            <LogoutIcon />
          </button>
        )}

        <span className="hide-mobile" style={{
          fontFamily: "'DM Mono', monospace", fontSize: '11px', color: t.textDim,
        }}>v1.0</span>
      </div>
    </header>
  );
}
